/**
 * Gate for the SA Devices and Device Assignments pages.
 *
 * Explicit V2 REACT_APP_STAGE / REACT_APP_ENV equality only — never NODE_ENV.
 * CRA production-mode builds set NODE_ENV=production for both staging and
 * production UIs; NODE_ENV cannot distinguish them.
 *
 * Phase-1 production (Owner 2026-10-03):
 *   LIST/READ  — ON  (GET /v1/sa/device-fleet/devices)
 *   COMMANDS   — OFF (Remote Console Update/Restart/Reboot)
 *   PREVIEW    — FORBIDDEN
 *
 * Staging keeps list + commands (existing QA). Preview remains staging/dev only.
 *
 * List/read does not call PubNub and does not require KK_APP_* SSM.
 * Commands remain fail-closed in the UI here and in the Device Fleet API.
 */

function testOverride(name: string): boolean | undefined {
  const value = process.env[name];
  if (value === "true") return true;
  if (value === "false") return false;
  return undefined;
}

/** Direct equality so CRA/webpack can constant-fold per-stage builds. */
const IS_V2_STAGING =
  process.env.REACT_APP_STAGE === "staging" ||
  process.env.REACT_APP_ENV === "staging";

const IS_V2_PRODUCTION =
  process.env.REACT_APP_STAGE === "production" ||
  process.env.REACT_APP_ENV === "production";

/**
 * Device Fleet list/read: staging and production V2 admin stages.
 * Development CRA uses REACT_APP_STAGE=staging (see .env-cmdrc).
 */
export const DEVICE_FLEET_LIST_ENABLED = IS_V2_STAGING || IS_V2_PRODUCTION;

/**
 * Remote Console commands: staging only until production OTA is authorized.
 * Production equality is listed so a production CRA build constant-folds this
 * to false (command handlers never ship as live).
 */
export const DEVICE_FLEET_COMMANDS_ENABLED =
  !IS_V2_PRODUCTION && IS_V2_STAGING;

/** @deprecated Use DEVICE_FLEET_LIST_ENABLED — list/read, not commands. */
export const DEVICE_FLEET_CONNECTED = DEVICE_FLEET_LIST_ENABLED;

export function isDeviceFleetListEnabled(): boolean {
  const override =
    testOverride("KK_TEST_DEVICE_FLEET_LIST") ??
    testOverride("KK_TEST_DEVICE_FLEET_CONNECTED");
  if (override !== undefined) return override;
  return DEVICE_FLEET_LIST_ENABLED;
}

export function isDeviceFleetCommandsEnabled(): boolean {
  const override = testOverride("KK_TEST_DEVICE_FLEET_COMMANDS");
  if (override !== undefined) return override;
  return DEVICE_FLEET_COMMANDS_ENABLED;
}

/** List/read only. Does not enable Remote Console commands. */
export function isDeviceFleetConnected(): boolean {
  return isDeviceFleetListEnabled();
}

export const PRODUCTION_COMMANDS_DISABLED_NOTICE =
  "Remote Console commands are not enabled in production. OTA remains gated.";

export const isDevicePreviewAllowed = (): boolean => {
  if (
    process.env.REACT_APP_STAGE === "production" ||
    process.env.REACT_APP_ENV === "production"
  ) {
    return false;
  }
  return (
    process.env.REACT_APP_STAGE === "staging" ||
    process.env.REACT_APP_ENV === "staging" ||
    process.env.REACT_APP_ENV === "development"
  );
};

export type DevicePreviewFixtures = typeof import("./devicePreviewFixtures");

export const loadDevicePreviewFixtures =
  (): Promise<DevicePreviewFixtures | null> => {
    if (
      process.env.REACT_APP_STAGE === "production" ||
      process.env.REACT_APP_ENV === "production"
    ) {
      return Promise.resolve(null);
    }
    if (
      process.env.REACT_APP_STAGE === "staging" ||
      process.env.REACT_APP_ENV === "staging" ||
      process.env.REACT_APP_ENV === "development"
    ) {
      return import("./devicePreviewFixtures");
    }
    return Promise.resolve(null);
  };
